import type { Filter, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import { FilterOperator, useFieldTypeTemplates, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { relatedItemKeyFields, resolveSelectedRelationTarget, serializeRelatedItemMatch } from '#layers/filters/components/Filter/utils/related-item-target';
import { useRelatedItemDrawer } from '#layers/filters/composables/use-related-item-drawer';
import { resolveFilterComponent } from '#layers/ui/public';

/**
 * Opens the item-selection drawer for a relation and builds the related-item match from the chosen
 * items, e.g. `{ author_id: { _eq: 42 } }` on a foreign key or `{ articles: { _some: { id: { _in: [1, 2] } } } }`
 * through a to-many relation.
 */
export function useRelatedItemFilter() {
	const openRelatedItemDrawer = useRelatedItemDrawer();
	const schemaStore = useWorkspaceSchemaStore();
	const filterSchema = injectFilterSchemaContext();
	const fieldTypeTemplates = useFieldTypeTemplates();

	function terminalInlinePicker(relationId: GlobalSingleRelationFieldId) {
		const templateId = filterSchema.findSingleRelationFieldById(relationId)?.meta?.typeTemplateId;
		const template = templateId ? fieldTypeTemplates.value[templateId] : undefined;
		return template?.type === 'relation' ? resolveFilterComponent(template.filter?.picker) : undefined;
	}

	async function buildRelatedItemCondition(relationFieldPath: string[]): Promise<Filter | null> {
		const selected = resolveSelectedRelationTarget(relationFieldPath, schemaStore);
		if (!selected) return null;
		const { target, selectedRelationId, isList } = selected;

		// An inline picker (e.g. the user popover) edits keys in place, so seed an empty match; the row
		// detects the inline picker and opens it, with no drawer round-trip on creation.
		if (terminalInlinePicker(selectedRelationId)) {
			return serializeRelatedItemMatch(target, isList ? FilterOperator.IN : FilterOperator.EQ, []);
		}

		const selection = await openRelatedItemDrawer(target.targetCollection.id, { selection: [], selectionKeys: relatedItemKeyFields(target) });

		if (!selection || selection.length === 0) return null;

		// A to-many relation (or several picks) is a membership match (`_in`); a single pick on a
		// to-one relation is an identity match (`_eq`).
		const operator = isList || selection.length > 1 ? FilterOperator.IN : FilterOperator.EQ;

		return serializeRelatedItemMatch(target, operator, selection);
	}

	// `openRelatedItemDrawer` is re-exposed so the in-row picker (reopening the drawer to edit an
	// existing related-item match) uses the same injected opener as the creation path.
	return { buildRelatedItemCondition, openRelatedItemDrawer };
}
